import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { attendanceApi } from '../../services/api';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { QrCode, LogIn, LogOut, MapPin, CheckCircle2, Camera } from 'lucide-react';

const extractToken = (text) => {
  if (!text) return null;
  try {
    const u = new URL(text);
    return u.searchParams.get('token');
  } catch {
    // düz token da olabilir
    return text;
  }
};

const CheckinPage = () => {
  const { toast } = useToast();
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get('token') || '');
  const [scanning, setScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const scannerRef = useRef(null);

  const loadStatus = async () => {
    try {
      const res = await attendanceApi.myStatus();
      setStatus(res.data?.data || null);
    } catch { /* yut */ }
  };
  useEffect(() => { loadStatus(); }, []);

  // URL'de token varsa otomatik işle
  useEffect(() => {
    if (params.get('token')) doCheckin(params.get('token'));
    /* eslint-disable-next-line */
  }, []);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch { /* yut */ }
      try { await scannerRef.current.clear(); } catch { /* yut */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScanner = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const qr = new Html5Qrcode('qr-reader');
        scannerRef.current = qr;
        await qr.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: 250 },
          async (decodedText) => {
            const t = extractToken(decodedText);
            await stopScanner();
            if (t) { setToken(t); doCheckin(t); }
          },
          () => {}
        );
      } catch (e) {
        toast({ title: 'Hata', description: 'Kamera açılamadı. İzin verdiğinizden emin olun.', variant: 'destructive' });
        setScanning(false);
      }
    }, 100);
  };

  const getPosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve({}),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    });

  const doCheckin = async (tok) => {
    const useToken = tok || token;
    if (!useToken) {
      toast({ title: 'Hata', description: 'Önce QR okutun', variant: 'destructive' });
      return;
    }
    setProcessing(true);
    try {
      const pos = await getPosition();
      const res = await attendanceApi.checkin({ token: useToken, ...pos });
      setResult(res.data?.data);
      toast({ title: 'Başarılı', description: res.data?.message, variant: 'success' });
      loadStatus();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.message || 'İşlem başarısız', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-6">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Giriş / Çıkış</h1>
        <p className="text-sm text-slate-500 mt-1">Yoklama noktasındaki QR kodu okutun</p>
      </div>

      {/* Durum kartı */}
      {status?.personnel && (
        <div className={`rounded-xl p-4 mb-5 text-white ${status.currentlyIn ? 'bg-emerald-600' : 'bg-slate-600'}`}>
          <p className="text-sm opacity-90">{status.personnel.name}</p>
          <p className="text-lg font-bold">{status.currentlyIn ? 'İçeride (giriş yapıldı)' : 'Dışarıda'}</p>
          <p className="text-xs opacity-80 mt-1">
            Bugün {status.records?.length || 0} hareket · Çalışılan: {status.worked_hours ?? 0} saat
          </p>
        </div>
      )}

      {/* Son işlem sonucu */}
      {result && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-5 flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
          <div>
            <p className="font-semibold text-emerald-800">
              {result.type === 'in' ? 'Giriş kaydedildi' : 'Çıkış kaydedildi'}
            </p>
            <p className="text-xs text-emerald-700">
              {new Date(result.time).toLocaleTimeString('tr-TR')} — {result.personnel}
            </p>
          </div>
        </div>
      )}

      {/* QR okuyucu */}
      {scanning ? (
        <div className="mb-4">
          <div id="qr-reader" className="rounded-xl overflow-hidden border border-slate-200" />
          <Button variant="outline" className="w-full mt-3" onClick={stopScanner}>Taramayı Durdur</Button>
        </div>
      ) : (
        <Button onClick={startScanner} className="w-full h-12 bg-blue-600 hover:bg-blue-700 mb-3">
          <Camera className="h-5 w-5 mr-2" /> Kamera ile QR Tara
        </Button>
      )}

      {/* Token elde varsa hızlı buton */}
      {token && !scanning && (
        <Button onClick={() => doCheckin()} disabled={processing} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700">
          {processing ? (
            <>İşleniyor...</>
          ) : status?.currentlyIn ? (
            <><LogOut className="h-5 w-5 mr-2" /> Çıkış Yap</>
          ) : (
            <><LogIn className="h-5 w-5 mr-2" /> Giriş Yap</>
          )}
        </Button>
      )}

      <p className="text-[11px] text-slate-400 text-center mt-4 flex items-center justify-center gap-1">
        <MapPin className="h-3 w-3" /> Konum ve IP adresiniz kayıt için alınır.
      </p>

      {/* Bugünkü hareketler */}
      {status?.records?.length > 0 && (
        <div className="mt-6">
          <p className="text-xs font-semibold uppercase text-slate-400 mb-2">Bugünkü Hareketler</p>
          <div className="space-y-1.5">
            {status.records.map((r, i) => (
              <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm">
                <span className={`flex items-center gap-1.5 font-medium ${r.type === 'in' ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {r.type === 'in' ? <LogIn className="h-3.5 w-3.5" /> : <LogOut className="h-3.5 w-3.5" />}
                  {r.type === 'in' ? 'Giriş' : 'Çıkış'}
                </span>
                <span className="text-slate-500">{new Date(r.created_at).toLocaleTimeString('tr-TR')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckinPage;
